import { Component, effect, inject, input, type OnDestroy, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { ColumnTypes } from "@platform/ingest/data-grid/data-grid.component";
import { AlertService, Button, ButtonIcon, Icon, Select, TextInput } from "@ui";
import { distinctUntilChanged, type Subscription, skip } from "rxjs";
import { ES_TYPE } from "../../services/elastic-search.service";
import { TRANSFORMER_ACTION, TRANSFORMER_STATE } from "../abstract-transformer.service";
import type { TransformerToolbar } from "../toolbar.interface";
import { TransformerService } from "../transformer.service";
import { SentenceChunkService } from "./sentence-chunk.service";

interface ColumnOption {
  id: string;
  name: string;
}

@Component({
  selector: "app-sentence-chunk-toolbar",
  template: `
    <div class="p-flex p-flex-col p-h-full p-min-h-full">
      <div class="p-flex p-flex-center-y p-p-xxs">
        <p-button-icon-ghosted-lg icon="arrow-left" (click)="cancel()" />
        <span class="p-text-body">Split</span>
      </div>

      <div class="p-divider"></div>

      <div class="p-flex-col-md p-p-md split-content">
        <div class="p-flex-col-xxs">
          <span class="p-text-desc">Column</span>
          <p-select
            trackBy="id"
            placeholder="Select column"
            [disabled]="!canEdit()"
            [options]="textColumnOptions"
            [ngModel]="selectedColumn()"
            (ngModelChange)="onColumnChange($event)"
          />
        </div>

        <div class="p-flex-col-xxs">
          <span class="p-text-desc">Sentences per segment</span>
          <p-text-input
            [disabled]="!canEdit()"
            [state]="isValidSize() || !sizeText() ? 'default' : 'error'"
            [ngModel]="sizeText()"
            (ngModelChange)="onSizeChange($event)"
          />
        </div>
      </div>

      <div class="p-divider"></div>

      <div class="p-flex-col-xs p-px-md p-py-sm split-footer">
        <p-button-primary-md [loading]="isPending" [disabled]="!canSave()" (click)="save()">
          @if (!isPending) {
            <p-icon-lg icon="checkmark" />
          }
          Save changes
        </p-button-primary-md>
        <p-button-secondary-md (click)="cancel()"><p-icon-lg icon="cross" />Cancel</p-button-secondary-md>
      </div>
    </div>
  `,
  styleUrls: ["./sentence-chunk-toolbar.component.scss"],
  imports: [FormsModule, Button, ButtonIcon, Icon, Select, TextInput],
})
export class SentenceChunkToolbarComponent implements TransformerToolbar, OnDestroy {
  private service = inject(SentenceChunkService);
  private transformerService = inject(TransformerService);
  private alertService = inject(AlertService);

  readonly corpus = input.required<Corpus>();

  protected readonly sizeText = signal("");
  textColumnOptions: ColumnOption[] = [];

  private subscription: Subscription;

  constructor() {
    // corpus can be reassigned to a new object (e.g. after a save); recompute options when it changes.
    effect(() => {
      const corpus = this.corpus();
      const types = this.service.columnTypes;
      if (!types) {
        throw new Error("Column types not set in SentenceChunkService");
      }
      this.textColumnOptions = corpus.columnMeta.columnOrder
        .filter((colId: keyof ColumnTypes) => types[colId]?.type === ES_TYPE.TEXT)
        .map((colId: string) => ({ id: colId, name: corpus.columnMeta.columns[colId].name }));
    });

    this.subscription = this.service.sentencesPerChunk$.subscribe((count) => {
      this.sizeText.set(count.toString());
    });

    // skip(1) drops the replayed current state, which is already DONE when editing an existing transform.
    this.subscription.add(
      this.service.state$.pipe(skip(1), distinctUntilChanged()).subscribe((state) => {
        if (state === TRANSFORMER_STATE.ERROR) {
          this.alertService.error("Problem segmenting data.");
        } else if (state === TRANSFORMER_STATE.DONE) {
          this.alertService.success("Changes saved.");
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  selectedColumn(): ColumnOption | null {
    return this.textColumnOptions.find((option) => option.id === this.service.columnId) ?? null;
  }

  onColumnChange(option: ColumnOption | null): void {
    const id = option?.id ?? null;
    if (id !== this.service.columnId) {
      this.service.columnId = id;
    }
  }

  onSizeChange(value: string): void {
    this.sizeText.set(value);
    const size = this.parsedSize();
    if (size !== null && size !== this.service.sentencesPerChunk) {
      this.service.sentencesPerChunk = size;
    }
  }

  private parsedSize(): number | null {
    if (!/^\d+$/.test(this.sizeText())) {
      return null;
    }
    const size = Number(this.sizeText());
    return Number.isSafeInteger(size) ? size : null;
  }

  isValidSize(): boolean {
    const size = this.parsedSize();
    return size !== null && size > 0;
  }

  canEdit(): boolean {
    return this.service.state !== TRANSFORMER_STATE.PENDING;
  }

  get isPending(): boolean {
    return this.service.state === TRANSFORMER_STATE.PENDING;
  }

  canSave(): boolean {
    return this.isValidSize() && this.service.state === TRANSFORMER_STATE.EDITING && !!this.service.columnId;
  }

  save(): void {
    this.service.doAction(TRANSFORMER_ACTION.SAVE);
  }

  cancel(): void {
    this.transformerService.cancel();
  }
}
